import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import _ from 'lodash';
import { BehaviorSubject, filter, forkJoin, of, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  getViolationRuleToAttackLinks,
  getViolationRuleToGenAiMatrixLinks,
  getViolationRuleToOwaspLinks,
} from 'src/app/common/violationRulesUtils';
import { AggregationsModel } from 'src/app/models/api/aggregations.model';
import {
  AIDRAggregations,
  AggregationDict,
  AggregationsType,
  AggregationType,
} from 'src/app/models/api/aggregations.model';
import { RuleCategory, RuleStatus, Severity, ViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { AttackSelectOptions } from 'src/app/models/selectOptions/attackSelectOptions.model';
import { OwaspSelectOptions } from 'src/app/models/selectOptions/owaspSelectOptions.model';
import { ViolationRulesViewModel } from 'src/app/models/viewModels/violationRulesViewModel';
import { ViolationRulesActionsService } from 'src/app/services/actions/violation-rules/violation-rules-actions.service';
import { ViolationRulesSupportedActions } from 'src/app/services/actions/violation-rules/violationRulesSupportedAction';
import { AggregationsHttpServiceService } from 'src/app/services/httpServices/aggregationsHttpService/aggregations-http-service.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { StatusLabelEnum } from '../status-label/status-label.component';
import { RowActionClicked } from '../table-row/table-row.component';
import { OwaspLlmSelectOptions } from 'src/app/models/selectOptions/owaspLlmSelectOptions.model';
import { OwaspAsiSelectOptions } from 'src/app/models/selectOptions/owaspAsiSelectOptions.model';
import { OwaspNhiSelectOptions } from 'src/app/models/selectOptions/owaspNhiSelectOptions.model';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { GenAiMatrixSelectOptions } from 'src/app/models/selectOptions/genAiMatrixSelectOptions.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccessService } from 'src/app/services/infra/access/access.service';
import { AccountCategory } from 'src/app/models/api/accountManagement.model';
import { AccountService } from 'src/app/services/infra/account/account.service';
import { RuleType } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ViolationRulesFiltersModel, ViolationRulesFiltersValues } from './filters/violation-rules-filters.model';
import { isAiRule } from 'src/app/common/aiServiceUtils';
import { DetectionRulesHttpService } from 'src/app/services/httpServices/governance/detectionRulesHttpService/detection-rules-http.service';

export enum SectionType {
  OWASP = 'owasp',
  OWASP_LLM = 'owaspLlm',
  OWASP_ASI = 'owaspAsi',
  OWASP_NHI = 'owaspNhi',
  MITRE = 'mitre',
  GEN_AI_MATRIX = 'genAiMatrix',
  NONE = 'none',
}

type RuleGroup = {
  id: string;
  value: string;
  link?: string;
  displayName?: string;
};

@Component({
  selector: 'app-violation-rules',
  templateUrl: './violation-rules.component.html',
  styleUrls: ['./violation-rules.component.scss'],
})
export class ViolationRulesComponent implements OnInit {
  public violationRules = input<ViolationRule[]>([]);
  public integrationId = input.required<string>();
  public policyId = input.required<string | null>();
  public refreshRequested = output<void>();

  public ruleType = input.required<RuleType>();

  public activeSection: WritableSignal<SectionType | null> = signal(null);
  public activeFilters: WritableSignal<ViolationRulesFiltersValues | null> = signal(null);
  public policiesAggregations: AggregationDict;
  public aidrPoliciesAggregations: AIDRAggregations;

  private readonly sortedViolationRules = computed(() => _.sortBy(this.violationRules(), ['name']));

  public viewModel: Signal<{
    rulesTables: Record<string, BehaviorSubject<BehaviorSubject<ViolationRulesViewModel>[]>>;
    ruleGroups: RuleGroup[];
  }>;

  public isLoading = signal<boolean>(false);

  public accountCategory$: Observable<AccountCategory | null>;
  public AccountCategoryEnum = AccountCategory;
  public RuleTypeEnum = RuleType;
  public isMockPolicyDetectionRulesFeatureEnabled: WritableSignal<boolean> = signal<boolean>(false);
  public isPoliciesRulesExportCsvButtonFeatureEnabled: WritableSignal<boolean> = signal<boolean>(false);

  private readonly _featureFlagsService = inject(FeatureFlagsService);

  public pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.Policies) as ViolationRulesFiltersModel;
  public isFilterMenuOpened = signal(false);

  protected sectionButtons: {
    section: SectionType;
    label: string;
  }[] = [
    {
      section: SectionType.NONE,
      label: 'policyPage.policy.violationRules.none',
    },
    {
      section: SectionType.OWASP_ASI,
      label: 'policyPage.policy.violationRules.owaspAsi',
    },
    ...(this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmOwaspNhi)
      ? [
          {
            section: SectionType.OWASP_NHI,
            label: 'policyPage.policy.violationRules.owaspNhi',
          },
        ]
      : []),
    {
      section: SectionType.OWASP,
      label: 'policyPage.policy.violationRules.owasp',
    },
    {
      section: SectionType.OWASP_LLM,
      label: 'policyPage.policy.violationRules.owaspLlm',
    },
    {
      section: SectionType.MITRE,
      label: 'policyPage.policy.violationRules.mitre',
    },
    ...(this.isMockPolicyDetectionRulesFeatureEnabled()
      ? [
          {
            section: SectionType.GEN_AI_MATRIX,
            label: 'policyPage.policy.violationRules.genAiMatrix',
          },
        ]
      : []),
  ];

  protected SectionType = SectionType;

  private _ruleGroups: Record<
    SectionType,
    { section: SectionType; group: RuleGroup[]; tableData: Record<string, ViolationRulesViewModel[]> }
  >;

  private _flatOptionsTableData: Record<string, ViolationRulesViewModel[]>;
  private _violationRulesOwaspPresentationTableData: Record<string, ViolationRulesViewModel[]>;
  private _violationRulesOwaspLlmPresentationTableData: Record<string, ViolationRulesViewModel[]>;
  private _violationRulesOwaspAsiPresentationTableData: Record<string, ViolationRulesViewModel[]>;
  private _violationRulesOwaspNhiPresentationTableData: Record<string, ViolationRulesViewModel[]>;
  private _violationRulesMitrePresentationTableData: Record<string, ViolationRulesViewModel[]>;
  private _violationRulesGenAiMatrixPresentationTableData: Record<string, ViolationRulesViewModel[]>;

  // Cache for BehaviorSubjects to avoid recreation on every filter change
  private readonly _ruleSubjectCache = new Map<string, BehaviorSubject<ViolationRulesViewModel>>();
  private readonly _groupSubjectCache = new Map<string, BehaviorSubject<BehaviorSubject<ViolationRulesViewModel>[]>>();

  private readonly _severity: Record<Severity, number> = { low: 1, medium: 2, high: 3, informational: 0 };
  private readonly _ruleStatusLabel: Record<RuleStatus, StatusLabelEnum> = {
    [RuleStatus.on]: StatusLabelEnum.active,
    [RuleStatus.off]: StatusLabelEnum.inactive,
    [RuleStatus.unavailable]: StatusLabelEnum.inactive,
  };

  public accessService = inject(AccessService);
  private readonly _accountService = inject(AccountService);

  constructor(
    private readonly _aggregationsHttpService: AggregationsHttpServiceService,
    private readonly _violationRulesActionsService: ViolationRulesActionsService,
    private readonly _notificationService: NotificationsService,
    private readonly _filtersService: FiltersService,
    private readonly _detectionRulesHttpService: DetectionRulesHttpService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.viewModel = computed(() => this._viewModelBasedOnSectionAndFilters());

    effect(() => !!this.activeSection() && this.pageFilters.sectionChange(this.activeSection()!), {
      allowSignalWrites: true,
    });
  }

  ngOnInit(): void {
    this.isLoading.set(true);

    // Initialize filters with integration and policy IDs
    const policyId = this.policyId();
    if (policyId) {
      this.pageFilters.initializeFilters(this._detectionRulesHttpService, this.integrationId(), policyId);
    }

    this.accountCategory$ = this._accountService.getAccountCategory$();
    this.isMockPolicyDetectionRulesFeatureEnabled.set(
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.MockPolicyDetectionRules)
    );
    this.isPoliciesRulesExportCsvButtonFeatureEnabled.set(
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.PoliciesRulesExportCsvButton)
    );

    const policiesAggregations$ = this._aggregationsHttpService
      .getAggregations(AggregationsType.common, {
        policy: [this.policyId()],
      } as ISelectedFilters)
      .pipe(
        catchError((error) => {
          this._notificationService.error('Could not load SPM aggregations.');
          console.error('Error fetching SPM aggregations:', error);
          return of({ aggregationsData: {} } as AggregationsModel);
        })
      );

    // Load AIDR aggregations if feature flag is enabled
    const aidrAggregations$ = this.isMockPolicyDetectionRulesFeatureEnabled()
      ? this._aggregationsHttpService.getAIDRAggregations(this.integrationId(), 100).pipe(
          catchError((error) => {
            this._notificationService.error('Could not load AIDR aggregations.');
            console.error('Error fetching AIDR aggregations:', error);
            return of({} as AIDRAggregations);
          })
        )
      : of({} as AIDRAggregations);

    forkJoin([policiesAggregations$, aidrAggregations$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([policiesAggregations, aidrAggregations]) => {
        this.policiesAggregations = (policiesAggregations.aggregationsData[
          AggregationType.violationsRuleAmountPerPolicy
        ]?.data ?? {}) as AggregationDict;
        this.aidrPoliciesAggregations = aidrAggregations;
        if (
          this._featureFlagsService.isFeatureEnabled(
            FeatureFlagTypes.SpmIamDemoAmex
          )
        ) {
          this._injectIamDemoAggregations();
        }
        this._createViolationsRulesMapping();
      });
  }

  public actionOnClick(actionClicked: RowActionClicked): void {
    const violationRuleViewModel = actionClicked.row.getValue() as ViolationRulesViewModel;

    const name = actionClicked.action.name as ViolationRulesSupportedActions;
    const response = this._violationRulesActionsService.handleActionClicked(
      name,
      _.keyBy(this.sortedViolationRules(), 'id')[violationRuleViewModel.rowId]
    );

    switch (actionClicked.action.name) {
      case ViolationRulesSupportedActions.Edit:
        response.pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef)).subscribe({
          next: () => {
            this._notificationService.success("Violation Rule's severity changed successfully");
            this.refreshRequested.emit();
          },
          error: () => this._notificationService.error("Could not change Violation Rule's severity"),
        });
        break;
      case ViolationRulesSupportedActions.Reset:
        response.pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef)).subscribe({
          next: () => {
            this._notificationService.success('Violation Rule reset successfully');
            this.refreshRequested.emit();
          },
          error: () => this._notificationService.error('Could not reset Violation Rule'),
        });
        break;
      default:
        break;
    }
  }

  public onFilterSelection() {
    this.activeFilters.set(this.pageFilters.getSelectedFilters());
  }

  private _viewModelBasedOnSectionAndFilters() {
    const activeSection = this.activeSection();
    const activeFilters = this.activeFilters();

    const groupFilters = new Set(activeFilters?.group);
    const tagsFilters = new Set(activeFilters?.tags);
    const statusFilters = new Set(activeFilters?.status);
    const severityFilters = new Set(activeFilters?.severity);
    const aiServiceFilters = new Set(activeFilters?.aiService);
    const eventDrivenSupportFilters = new Set(activeFilters?.eventDrivenSupport);

    // Pre-compute a Map for O(1) lookups instead of O(n) .find() calls
    const rulesById = new Map(this.sortedViolationRules().map((r) => [r.id, r]));

    let rulesTables: Record<string, BehaviorSubject<BehaviorSubject<ViolationRulesViewModel>[]>> = {};
    let ruleGroups: RuleGroup[] = [];

    const toTableData = (rules: Record<string, ViolationRulesViewModel[]>) => {
      const result: Record<string, BehaviorSubject<BehaviorSubject<ViolationRulesViewModel>[]>> = {};
      for (let key in rules) {
        const filtered = rules[key]
          .filter((rule) => {
            // O(1) lookup for aiService filter
            const originalRule = rulesById.get(rule.rowId);
            const matchesAiService =
              !aiServiceFilters.size || (originalRule?.aiService && aiServiceFilters.has(originalRule.aiService));

            return (
              (!tagsFilters.size || rule.tags.find((tag) => tagsFilters.has(tag))) &&
              (!statusFilters.size || statusFilters.has(rule.status)) &&
              (!severityFilters.size || severityFilters.has(rule.severity)) &&
              matchesAiService &&
              (!eventDrivenSupportFilters.size ||
                eventDrivenSupportFilters.has(String(rule.eventDrivenSupport ?? false)))
            );
          })
          .map((rule) => {
            // Reuse existing BehaviorSubject from cache or create new one
            const cacheKey = rule.rowId;
            let ruleSubject = this._ruleSubjectCache.get(cacheKey);
            if (ruleSubject) {
              ruleSubject.next(rule);
            } else {
              ruleSubject = new BehaviorSubject(rule);
              this._ruleSubjectCache.set(cacheKey, ruleSubject);
            }
            return ruleSubject;
          });

        // Reuse existing group BehaviorSubject from cache or create new one
        let groupSubject = this._groupSubjectCache.get(key);
        if (groupSubject) {
          groupSubject.next(filtered);
        } else {
          groupSubject = new BehaviorSubject(filtered);
          this._groupSubjectCache.set(key, groupSubject);
        }
        result[key] = groupSubject;
      }
      return result;
    };

    if (activeSection) {
      const { tableData, group } = this._ruleGroups[activeSection];
      rulesTables = toTableData(tableData);
      ruleGroups = groupFilters.size ? group.filter((group) => groupFilters.has(group.value)) : group;
    }

    return { rulesTables, ruleGroups };
  }

  private _getLinkOfCategory(value: string) {
    const currentActiveSection = this.activeSection();
    if (
      currentActiveSection === SectionType.OWASP ||
      currentActiveSection === SectionType.OWASP_LLM ||
      currentActiveSection === SectionType.OWASP_ASI ||
      currentActiveSection === SectionType.OWASP_NHI
    ) {
      return getViolationRuleToOwaspLinks(value);
    } else if (currentActiveSection === SectionType.GEN_AI_MATRIX) {
      return getViolationRuleToGenAiMatrixLinks(value);
    } else {
      return getViolationRuleToAttackLinks(value);
    }
  }

  private _createMappingByCategoryTablesData(sectionType: SectionType): {
    [category: string]: ViolationRulesViewModel[];
  } {
    const rulesMapping: { [category: string]: ViolationRule[] } = {};

    switch (sectionType) {
      case SectionType.OWASP:
        this._setRulesMapping(rulesMapping, 'owasp', 'LCNC');
        break;
      case SectionType.OWASP_LLM:
        this._setRulesMapping(rulesMapping, 'owasp', 'LLM');
        break;
      case SectionType.OWASP_ASI:
        this._setRulesMapping(rulesMapping, 'owasp', 'ASI');
        break;
      case SectionType.OWASP_NHI:
        this._setRulesMapping(rulesMapping, 'owasp', 'NHI');
        break;
      case SectionType.MITRE:
        this._setRulesMapping(rulesMapping, 'attack', '');
        break;
      case SectionType.GEN_AI_MATRIX:
        this._setRulesMapping(rulesMapping, 'attack', '');
        break;
      case SectionType.NONE:
        this._setRulesMapping(rulesMapping, 'none', '');
        break;
      default:
        console.error('Invalid sectionType=' + sectionType + ' in _createMappingByCategoryTablesData!');
    }

    const result: Record<string, ViolationRulesViewModel[]> = {};

    for (let key in rulesMapping) {
      result[key] = rulesMapping[key].map((rule) => this.toViolationRuleModel(rule));
    }
    return result;
  }

  private toViolationRuleModel(rule: ViolationRule): ViolationRulesViewModel {
    const isNotHygiene = rule.category !== RuleCategory.hygiene;
    const isRuleAi = isAiRule(rule);

    let violationsAmount = this.policiesAggregations?.[rule.id] || 0;
    if (isRuleAi && this.aidrPoliciesAggregations?.[rule.id]) {
      violationsAmount = this.aidrPoliciesAggregations[rule.id];
    }

    let violationsEndpointRoute: string | null = null;
    let violationsEndpointQuery: Record<string, string> = {};

    if (isRuleAi) {
      violationsEndpointRoute = '/app/ai-findings';
      const integrationId = this.integrationId();
      violationsEndpointQuery = {
        q: `search:"${rule.id}" integration:${integrationId}`,
        view: 'individuals',
      };
    } else {
      violationsEndpointRoute = isNotHygiene ? '/app/violations' : '/app/hygiene';
      const integrationId = this.integrationId();
      const policyId = this.policyId();
      violationsEndpointQuery = {
        q: `integration:${integrationId} violationRule:${rule.id} violationStatus:open policy:${policyId}`,
      } as Record<string, string>;
    }

    const status = rule.isAvailable ? rule.status : RuleStatus.unavailable;

    const rowTooltipText =
      rule.isAvailable === false ? `Contact us to enable AI Detection & Response for this AI service` : undefined;

    return {
      rowId: rule.id,
      integrationId: this.integrationId(),
      policyId: this.policyId(),
      tags: rule.tags,
      ruleDescription: rule.description,
      clickable: !!rule.isAvailable,
      ruleName: rule.displayName,
      ruleId: rule.id,
      count: violationsAmount,
      violations: violationsAmount,
      violationsEndpointRoute: rule.isAvailable ? violationsEndpointRoute : undefined,
      violationsEndpointQuery: violationsEndpointQuery,
      severity: rule.severity,
      severityValue: this._severity[rule.severity],
      status: status,
      statusLabel: this._ruleStatusLabel[status],
      modified: !!rule.userActionTimeStamps,
      editIcon: 'assets/images/common/dots.svg',
      menuActions:
        rule.isAvailable && !isRuleAi ? this._violationRulesActionsService.getMenuActionForViolationRules(rule) : [],
      isAvailable: rule.isAvailable,
      className: rule.isAvailable === false ? 'unavailable-rule' : undefined,
      rowTooltipText: rowTooltipText,
      eventDrivenSupport: rule.eventDrivenSupport,
      platform: rule.platform,
    };
  }

  private _setRulesMapping(
    rulesMapping: { [category: string]: ViolationRule[] },
    ruleCategory: 'owasp' | 'attack' | 'none',
    ruleClassificationPrefix: 'LCNC' | 'LLM' | 'ASI' | 'NHI' | ''
  ) {
    // Filter to only include available rules (hide inactive rules for platforms not enabled)
    const availableRules = this.sortedViolationRules().filter((rule) => rule.isAvailable !== false);

    if (ruleCategory === 'none') {
      rulesMapping['none'] = availableRules;
      return;
    }
    for (const rule of availableRules) {
      const classifications = rule[ruleCategory as keyof ViolationRule] as string[];
      if (Array.isArray(classifications)) {
        for (const rule_classification of classifications) {
          if (rule_classification.startsWith(ruleClassificationPrefix)) {
            if (!rulesMapping[rule_classification]) {
              rulesMapping[rule_classification] = [];
            }
            rulesMapping[rule_classification].push(rule);
          }
        }
      }
    }
  }

  private _injectIamDemoAggregations(): void {
    const mockCounts: Record<string, number> = {
      ZN_HG00029: 1,
      ZN_NHI00005: 1,
      ZN_HG00043: 1,
      ZN_HG00030: 1,
      ZN_HG00071: 1,
      ZN_HG00072: 1,
    };
    for (const [ruleId, count] of Object.entries(mockCounts)) {
      if (!this.policiesAggregations[ruleId]) {
        this.policiesAggregations[ruleId] = count;
      }
    }
  }

  private _createViolationsRulesMapping() {
    this._flatOptionsTableData = this._createMappingByCategoryTablesData(SectionType.NONE);
    this._violationRulesOwaspPresentationTableData = this._createMappingByCategoryTablesData(SectionType.OWASP);
    this._violationRulesOwaspLlmPresentationTableData = this._createMappingByCategoryTablesData(SectionType.OWASP_LLM);
    this._violationRulesOwaspAsiPresentationTableData = this._createMappingByCategoryTablesData(SectionType.OWASP_ASI);
    this._violationRulesOwaspNhiPresentationTableData = this._createMappingByCategoryTablesData(SectionType.OWASP_NHI);
    this._violationRulesMitrePresentationTableData = this._createMappingByCategoryTablesData(SectionType.MITRE);
    this._violationRulesGenAiMatrixPresentationTableData = this._createMappingByCategoryTablesData(
      SectionType.GEN_AI_MATRIX
    );

    const mapSelectOptionToGroup = ({ id, value, displayName }: ISelectOption): RuleGroup => ({
      id,
      value,
      displayName,
      link: this._getLinkOfCategory(value),
    });

    const createRuleGroup = (tableData: Record<string, ViolationRulesViewModel[]>, options: ISelectOption[]) => {
      const keysSet = new Set(Object.keys(tableData));

      return _.cloneDeep(options)
        .filter((option) => keysSet.has(option.value))
        .map(mapSelectOptionToGroup);
    };

    this._ruleGroups = {
      [SectionType.OWASP_ASI]: {
        section: SectionType.OWASP_ASI,
        group: createRuleGroup(this._violationRulesOwaspAsiPresentationTableData, OwaspAsiSelectOptions),
        tableData: this._violationRulesOwaspAsiPresentationTableData,
      },
      [SectionType.OWASP_NHI]: {
        section: SectionType.OWASP_NHI,
        group: createRuleGroup(this._violationRulesOwaspNhiPresentationTableData, OwaspNhiSelectOptions),
        tableData: this._violationRulesOwaspNhiPresentationTableData,
      },
      [SectionType.OWASP]: {
        section: SectionType.OWASP,
        group: createRuleGroup(this._violationRulesOwaspPresentationTableData, OwaspSelectOptions),
        tableData: this._violationRulesOwaspPresentationTableData,
      },
      [SectionType.OWASP_LLM]: {
        section: SectionType.OWASP_LLM,
        group: createRuleGroup(this._violationRulesOwaspLlmPresentationTableData, OwaspLlmSelectOptions),
        tableData: this._violationRulesOwaspLlmPresentationTableData,
      },
      [SectionType.MITRE]: {
        section: SectionType.MITRE,
        group: createRuleGroup(this._violationRulesMitrePresentationTableData, AttackSelectOptions),
        tableData: this._violationRulesMitrePresentationTableData,
      },
      [SectionType.GEN_AI_MATRIX]: {
        section: SectionType.GEN_AI_MATRIX,
        group: createRuleGroup(this._violationRulesGenAiMatrixPresentationTableData, GenAiMatrixSelectOptions),
        tableData: this._violationRulesGenAiMatrixPresentationTableData,
      },
      [SectionType.NONE]: {
        section: SectionType.NONE,
        group: [{ value: 'none', id: 'none' }],
        tableData: this._flatOptionsTableData,
      },
    };

    this.activeSection.set(SectionType.NONE);
    this.isLoading.set(false);
  }
}
